import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Package,
  Layers,
  TrendingUp,
  RotateCcw,
  Upload,
  LogOut,
  BarChart3,
  PieChart,
  MessageCircle,
  Settings as SettingsIcon,
  Link2,
  Database,
  Users,
  Tag,
  Rocket
} from 'lucide-react';
import { Button } from './ui/button';
import { Chatbot } from './Chatbot';

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [chatbotOpen, setChatbotOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Inventory', href: '/inventory', icon: Package },
    { name: 'Combos', href: '/combos', icon: Layers },
    { name: 'FBA Forecast', href: '/fba-forecast', icon: TrendingUp },
    { name: 'Reorder', href: '/reorder', icon: RotateCcw },
    { name: 'Upload Sales', href: '/upload', icon: Upload },
    { name: 'Data Management', href: '/data-management', icon: Database },
    { name: 'SKU Analysis', href: '/sku-analysis', icon: PieChart },
    { name: 'Launch Tracker', href: '/launch-tracker', icon: Rocket },
    { name: 'Deal Recommendations', href: '/deal-recommendations', icon: Tag },
    { name: 'Analytics', href: '/analytics', icon: BarChart3 },
    { name: 'SKU Mapping', href: '/sku-mapping', icon: Link2 },
    { name: 'Settings', href: '/settings', icon: SettingsIcon },
    // Admin-only navigation item
    ...(user?.role === 'admin' ? [{ name: 'User Management', href: '/user-management', icon: Users }] : []),
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-background">
      <aside className="w-64 bg-primary text-primary-foreground flex flex-col border-r border-border">
        <div className="p-6 border-b border-border/20">
          <h1 className="text-xl font-bold tracking-tight font-heading" data-testid="app-logo">RSO INVENTORY</h1>
          <p className="text-xs text-primary-foreground/70 mt-1 uppercase tracking-widest">Command Center</p>
        </div>
        
        <nav className="flex-1 p-4 space-y-1">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center gap-3 px-3 py-2 rounded-sm transition-colors ${
                  isActive
                    ? 'bg-primary-foreground text-primary font-medium'
                    : 'text-primary-foreground/80 hover:bg-primary-foreground/10 hover:text-primary-foreground'
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-sm">{item.name}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-border/20">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-sm bg-primary-foreground/20 flex items-center justify-center text-xs font-mono font-medium">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" data-testid="user-name">{user?.name}</p>
              <p className="text-xs text-primary-foreground/60 uppercase tracking-wide">{user?.role}</p>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            data-testid="logout-button"
            variant="ghost"
            className="w-full justify-start text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10"
          >
            <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Logout
          </Button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <div className="p-8">
          {children}
        </div>
        
        {!chatbotOpen && (
          <button
            onClick={() => setChatbotOpen(true)}
            data-testid="open-chatbot"
            className="fixed bottom-4 right-4 w-14 h-14 bg-primary text-primary-foreground rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform z-40"
          >
            <MessageCircle className="w-6 h-6" strokeWidth={1.5} />
          </button>
        )}
        
        <Chatbot isOpen={chatbotOpen} onClose={() => setChatbotOpen(false)} />
      </main>
    </div>
  );
};